import React, { useState, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { useNavigate, useLocation } from 'react-router-dom';
import getCurrencySymbol from '../lib/currenyUtils';
import { Constants } from '../lib/const/constants';
import axios from 'axios';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { ArrowLeft, ArrowRight } from 'lucide-react'
import { encryptPayload } from '../utils/encryption';

const PaymentOption = ({ id, title, subtitle, icon, selected, onClick }) => (
  <button
    type="button"
    className={`pm-option ${selected ? 'selected' : ''}`}
    onClick={() => onClick(id)}
    aria-pressed={selected}
  >
    <div className="pm-icon">{icon}</div>
    <div className="pm-body">
      <div className="pm-title">{title}</div>
      <div className="pm-sub">{subtitle}</div>
    </div>
    <div className="pm-check" aria-hidden>
      <span className={`pm-dot ${selected ? 'on' : ''}`} />
    </div>
  </button>
);

PaymentOption.propTypes = {
  id: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  icon: PropTypes.node,
  selected: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
};

PaymentOption.defaultProps = {
  subtitle: '',
  icon: null,
  selected: false,
};

export default function PaymentMethodWeb() {
  const navigate = useNavigate();
  const location = useLocation();
  const s = location.state || {};
  console.log("payment method params data", s)
  const currency = s.currency;
  const transfer_fee = s.transfer_fee;
  const service_fee = s.service_fee;

  // ── props coming from SendMoneyPaymentConfirmation via navigate state ──
  const name = s.name;
  // const ifsc             = s.ifsc;
  const code = s.code;
  const account = s.account;
  const type = s.type;
  const id = s.id;
  const bank_account_name = s.bank_account_name;
  const currencyid = s.currencyid;
  const contactId = s.contactId;
  const from_currency = s.from_currency;
  const from_amount = s.from_amount;
  const total_amount = s.total_amount;
  const amount = s.amount;
  const bank_account_id = s.bank_account_id;
  const country = s.country;
  const bene_type = s.bene_type;
  const bank_type = s.bank_type;
  const exchangeRate = s.exchangeRate ?? 1.0;
  const address = s.address;

  // ── file upload ref ──
  const fileInputRef = useRef(null);

  // ── state ──
  const [method, setMethod] = useState('');
  // const [subMethod, setSubMethod] = useState('');
  const [loading, setLoading] = useState(false);
  const [transferReason, settransferReason] = useState('');
  const [transferReasons, setTransferReasons] = useState([]);
  const [ref, setRef] = useState('');
  const [notes] = useState('');
  const [file, setFile] = useState(null);
  const [, setFileName] = useState('');
  const [uploadedDocs, setUploadedDocs] = useState([]);
  const [showDocs, setShowDocs] = useState(false);
  const [clearBankBalance] = useState(0.0);
  const [currencyCloudBalance] = useState(0.0);
  const [availableCredit, setAvailableCredit] = useState(0);
  const [creditLoading, ] = useState(false);

  const staffLogin = sessionStorage.getItem('staff_login_id');
  const isStaffAllowed = (Constants.STAFF_LOGIN_ID_CONVERSION || []).includes(
    staffLogin,
  );

  useEffect(() => {
    setAsyncData();
  }, []);

  const setAsyncData = async () => {
    sessionStorage.setItem('beneficiary_id', JSON.stringify(id));
    sessionStorage.setItem('beneficiary_bank_account_name', bank_account_name);
    setTransferReasons(JSON.parse(sessionStorage.getItem('transfer_reasons')) || []);
  };

  const setReasonForTransfer = (val) => settransferReason(val);

  // ── amount calculations ──
  // // ── amount calculations ──
  // const amount = useMemo(() => {
  //   const val = parseFloat(s.from_amount || s.total_amount);
  //   return Number.isNaN(val) ? 0 : val;
  // }, [s.from_amount]);

  const formattedAmount = useMemo(
    () =>
      Number(from_amount || 0).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
    [from_amount],
  );
  const formattedTotalAmount = useMemo(
    () =>
      Number(total_amount || 0).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
    [total_amount],
  );

  const recipientGets = useMemo(() => {
    // const val = amount * (parseFloat(exchangeRate) || 1);
    const val = Number(amount);
    return Number.isNaN(val) ? 0 : val;
  }, [amount]);

  const recipientDisplay = `${getCurrencySymbol(s.currency || '') || ''} ${recipientGets.toLocaleString(
    undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }
  )}`;

  // ── user type helper ──
  const getUserTypeFromSession = () => {
    try {
      const raw = sessionStorage.getItem('login_workspaces');
      if (!raw) return 'individual';
      const parsed = JSON.parse(raw);
      const typeValue = (parsed?.type || parsed?.[0]?.type || '').toString().trim();
      return typeValue || 'individual';
    } catch {
      return 'individual';
    }
  };

  const userType = getUserTypeFromSession();

  const handleMultiFileUpload = (e) => {
    const files = Array.from(e.target.files);
    const mapped = files.map((f, i) => ({
      id: Date.now() + i,
      name: f.name,
      file: f,
      url: URL.createObjectURL(f),
    }));
    setUploadedDocs((prev) => [...prev, ...mapped]);
    // also set the first file as the "single" file for upload API
    if (files[0]) { setFile(files[0]); setFileName(files[0].name); }
  };

  const getBase64 = (f, cb) => {
    const reader = new FileReader();
    reader.readAsDataURL(f);
    reader.onload = () => cb(reader.result);
    reader.onerror = (err) => console.log('Error: ', err);
  };

  const uploadSingleDocument = async (document, transactionId) => {
    if (!file) return;
    const token = sessionStorage.getItem('login_token');
    const extension = file.name.split('.');
    await axios
      .post(
        Constants.BASE_URL + 'API-FX-195-UPLOAD-USER-TRANSACTION-DOCUMENTS',
        {
          document,
          transaction_id: transactionId,
          notes: notes || 'NA',
          extension: extension[extension.length - 1],
        },
        {
          headers: {
            Authorization: 'Bearer ' + JSON.parse(token),
            fx_key: Constants.SUBSCRIPTION_KEY,
          },
        },
      )
      .then((res) => console.log(res.data))
      .catch((err) => console.error(err));
  };
  // const handlecredit = async () => {
  //   const token = sessionStorage.getItem('login_token');
  //   try {
  //     setCreditLoading(true);
  //     const response = await axios.get(
  //       Constants.BASE_URL + 'API-FX-251-GET-CREDIT-LINE-BALANCE-AGENET/1',
  //       {
  //         headers: {
  //           Authorization: 'Bearer ' + JSON.parse(token),
  //           fx_key: Constants.SUBSCRIPTION_KEY,
  //         },
  //       },
  //     );
  //     console.log('251 api response', response);
  //     setAvailableCredit(Number(response.data.amount));
  //   } catch (error) {
  //     console.log(error.response);
  //     toast.error('unable to fetch credit balance');
  //   } finally {
  //     setCreditLoading(false)
  //   }
  // };
  const handleFileSubmit = (transactionId) => {
    if (file) getBase64(file, (result) => uploadSingleDocument(result, transactionId));
  };

  // ── initiateWalletTransaction (ported 1-to-1 from SendMoneyPaymentConfirmation) ──
  const initiateWalletTransaction = async () => {
    setLoading(true);
    sessionStorage.removeItem('transfer_reason');

    const login_workspaces_id = sessionStorage.getItem('login_workspaces_id');
    const token = sessionStorage.getItem('login_token');
    const workspace = sessionStorage.getItem('login_workspaces');
    const parsedWorkspace = JSON.parse(workspace);
    const login_full_name = parsedWorkspace[0].name;
    const staff_login_id = sessionStorage.getItem('staff_login_id');

    if (!transferReason) {
      toast.error('Please select transfer reason');
      setLoading(false);
      return;
    }

    if (!method) {
      toast.error('Please select a payment method');
      setLoading(false);
      return;
    }
    if (method === 'credit') {
      const debitamount = Number(total_amount || from_amount)
      if (debitamount > Number(availableCredit)) {
        toast.error('Insufficient Balance');
        setLoading(false);
        return;
      }
    }


    if (method === 'manual') {
      setLoading(false);
      navigate('/manual-transfer', {
        state: {
          name, code, account, type, id,
          exchangeRate,
          bank_account_name, currencyid, currency, amount: s.amount,
          bank_account_id, country, bene_type, bank_type,
          contactId,
          from_currency, from_amount,
          address,
          transferReason,
          ref,
          transfer_fee,
          service_fee,
          method,
          formattedAmount,
          recipientGets,
          recipientDisplay,
        },
      });
      return;
    }

    // ── BANK: no 240 here — Banks.js calls it on Continue ──
    if (method === 'bank') {
      try {
        const payload = {
          name, code, account, type, id,
          exchangeRate,
          bank_account_name, currencyid, currency, amount: s.amount,
          bank_account_id, country, bene_type, bank_type,
          contactId,
          from_currency, from_amount,
          address,
          transferReason,
          ref,
          transfer_fee,
          service_fee,
        };
        const credittransactionid = await gettransactionid({ ...payload, from_amount, from_currency });
        await startBankPayment({ ...payload, from_amount, from_currency }, credittransactionid);
      } catch (err) {
        console.error('bank flow error:', err);
        toast.error('Unable to load banks: ' + (err.message || err));
      } finally {
        setLoading(false);
      }
      return;
    }

    // ── CARD / CREDIT: 240 is called here as before ──
    try {
      const encryptedPayload = await encryptPayload({
        contact_id: contactId,
        workspace_id: login_workspaces_id,
        reason_code: transferReason,
        full_name: bank_account_name,
        sort_code: code,
        createTransaction: true,
        currency,
        account_number: account,
        type: 'local',
        currency_cloud_balance: currencyCloudBalance,
        clear_bank_balance: clearBankBalance,
        total_balance: 0,
        beneficiaryId: id,
        reference: ref !== '' ? ref : login_full_name,
        reason: transferReason,
        staff_login_id,
        status: 'waiting for funds',
        flow: 'sendFlow',
        transfer_fee: transfer_fee,
        service_fee: service_fee,
        method: method,
        platform: 'web', extraParamValidation: 'trustedfromweb'
      });
      const resp = await axios.post(
        Constants.BASE_URL + 'API-FX-240-SEND-FLOW',
        encryptedPayload,
        {
          headers: {
            fx_key: Constants.SUBSCRIPTION_KEY,
            Authorization: 'Bearer ' + JSON.parse(token),
          },
        },
      );

      const transactionId = resp?.data?.data?.id;

      if (file) handleFileSubmit(transactionId);

      const payload = {
        name, code, account, type, id,
        exchangeRate,
        bank_account_name, currencyid, currency, amount: s.amount,
        bank_account_id, country, bene_type, bank_type,
        contactId,
        from_currency, from_amount,
        transactionId,
        address
      };

      if (method === 'card') {
        const login_workspaces_id = sessionStorage.getItem('login_workspaces_id');
        const clearBankCustomerSortCode = sessionStorage.getItem('clearBankCustomerSortCode');
        const clearBankCustomerWalletNumber = sessionStorage.getItem('clearBankCustomerWalletNumber');
        await axios
          .post(
            Constants.BASE_URL + 'API-FX-219-CHANGE-TRANSACTION-STATUS',
            {
              transaction_id: payload.transactionId,
              workspace_id: login_workspaces_id,
              status: 'card_payment_pending',
            },
            {
              headers: {
                fx_key: Constants.SUBSCRIPTION_KEY,
              },
            },
          )
          .then((resp) => {
            console.log(resp.data);
            setLoading(false);
            navigate('/PaymentSuccess', {
              replace: true,
              state: {
                name: payload.name || payload.bank_account_name || '',
                ifsc:
                  payload.from_currency === 'GBP'
                    ? clearBankCustomerSortCode ||
                    Constants.ACCOUNT_DETAILS[0]?.GBP?.[0]?.['Sort Code']
                    : '-',
                account:
                  payload.from_currency === 'GBP'
                    ? clearBankCustomerWalletNumber ||
                    Constants.ACCOUNT_DETAILS?.[0]?.GBP?.[0]?.['Account Number']
                    : '-',
                type: payload.type || payload.bene_type || '',
                id:
                  payload.id ||
                  payload.bank_account_id ||
                  payload.contactId ||
                  '',
                bank_account_name:
                  payload.name || payload.bank_account_name || '',
                currencyid: payload.currencyid,
                currency: payload.currency,
                amount: payload.amount || payload.transferAmount || '',
                bank_account_id: payload.bank_account_id || '',
                country: payload.country || '',
                bene_type: payload.bene_type || '',
                bank_type: payload.bank_type || '',
                contactId: payload.contactId,
                transfer_fee: transfer_fee,
                service_fee: service_fee
              },
            });
          });
      } else {
        const token = sessionStorage.getItem('login_token');
        const creditResponse = await axios.post(
          Constants.BASE_URL +
          'API-FX-250-CREATE-CREDIT-LINE-TRANSACTION-AGENT',
          {
            user_registered_domains_id: 1,
            amount: total_amount ? Number(total_amount) : Number(from_amount),
            type: 'debit',
          },
          {
            headers: {
              Authorization: 'Bearer ' + JSON.parse(token),
              fx_key: Constants.SUBSCRIPTION_KEY,
            },
          },
        );

        console.log('Credit Transaction:', creditResponse.data);
        setLoading(false);
        navigate('/PaymentSuccess', {
          state: {
            name: payload.name || payload.bank_account_name || '',
            ifsc: payload.code || '',
            account: payload.account || '',
            type: payload.type || payload.bene_type || '',
            id:
              payload.id || payload.bank_account_id || payload.contactId || '',
            bank_account_name: payload.name || payload.bank_account_name || '',
            currencyid: payload.currencyid,
            currency: payload.currency,
            amount: payload.amount,
            bank_account_id: payload.bank_account_id,
            country: payload.country,
            bene_type: payload.bene_type,
            bank_type: payload.bank_type,
            contactId: payload.contactId,
            transactionId: payload.transactionId,
            paymentMethod: 'credit',
            availableCredit,
          },
        });
      }
    } catch (err) {
      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        'Something went wrong. Please try again.';
      toast.error(errorMessage);
      setLoading(false);
    }

    //     try {
    //       const encryptedPayload = await encryptPayload({
    //           contact_id:              contactId,
    //           workspace_id:            login_workspaces_id,
    //           reason_code:             transferReason,
    //           full_name:               bank_account_name,
    //           // sort_code:               ifsc,
    //           sort_code:               code,
    //           createTransaction:       true,
    //           currency,
    //           account_number:          account,
    //           type:                    'local',
    //           currency_cloud_balance:  currencyCloudBalance,
    //           clear_bank_balance:      clearBankBalance,
    //           total_balance:           0,
    //           beneficiaryId:           id,
    //           reference:               ref !== '' ? ref : login_full_name,
    //           reason:                  transferReason,
    //           staff_login_id,
    //           status:                  'waiting for funds',
    //           flow:                    'sendFlow',
    //           transfer_fee: transfer_fee,
    //           service_fee: service_fee,
    //           method: method,
    //           platform: 'web',extraParamValidation: 'trustedfromweb'
    //         });
    //       const resp = await axios.post(
    //         Constants.BASE_URL + 'API-FX-240-SEND-FLOW',
    //         encryptedPayload
    //         ,
    //         {
    //           headers: {
    //             fx_key:        Constants.SUBSCRIPTION_KEY,
    //             Authorization: 'Bearer ' + JSON.parse(token),
    //           },
    //         },
    //       );

    //       const transactionId = resp?.data?.data?.id;

    //       // upload doc if any
    //       if (file) handleFileSubmit(transactionId);

    //       // now route based on selected method
    //       const payload = {
    //         name, code, account, type, id,
    //         exchangeRate,
    //         bank_account_name, currencyid, currency, amount: s.amount,
    //         bank_account_id, country, bene_type, bank_type,
    //         contactId,
    //         from_currency, from_amount,
    //         transactionId,
    //         address
    //       };

    //       if (method === 'bank') {
    //         setLoading(false);
    //         // startBankPayment({ ...payload, total_amount, from_currency });
    //         const credittransactionid = await gettransactionid(payload)
    //         startBankPayment({ ...payload, from_amount, from_currency }, credittransactionid);
    //       } else if (method === 'manual') {
    //         setLoading(false);
    //         navigate('/manual-transfer', {
    //           state: {
    //             ...payload,
    //             method,
    //             formattedAmount,
    //             exchangeRate,
    //             recipientGets,
    //             recipientDisplay,
    //             transfer_fee,
    //             service_fee
    //           },
    //         });
    //       } else if (method === 'card') {
    //         const login_workspaces_id = sessionStorage.getItem(
    //           'login_workspaces_id',
    //         );
    //         const clearBankCustomerSortCode = sessionStorage.getItem(
    //           'clearBankCustomerSortCode',
    //         );
    //         const clearBankCustomerWalletNumber = sessionStorage.getItem(
    //           'clearBankCustomerWalletNumber',
    //         );
    //         await axios
    //           .post(
    //             Constants.BASE_URL + 'API-FX-219-CHANGE-TRANSACTION-STATUS',
    //             {
    //               transaction_id: payload.transactionId,
    //               workspace_id: login_workspaces_id,
    //               status: 'card_payment_pending',
    //             },
    //             {
    //               headers: {
    //                 fx_key: Constants.SUBSCRIPTION_KEY,
    //               },
    //             },
    //           )
    //           .then((resp) => {
    //             console.log(resp.data);
    //             setLoading(false);
    //             navigate('/PaymentSuccess', {
    //               replace: true,
    //               state: {
    //                 name: payload.name || payload.bank_account_name || '',
    //                 ifsc:
    //                   payload.from_currency === 'GBP'
    //                     ? clearBankCustomerSortCode ||
    //                       Constants.ACCOUNT_DETAILS[0]?.GBP?.[0]?.['Sort Code']
    //                     : '-',
    //                 account:
    //                   payload.from_currency === 'GBP'
    //                     ? clearBankCustomerWalletNumber ||
    //                       Constants.ACCOUNT_DETAILS?.[0]?.GBP?.[0]?.[
    //                         'Account Number'
    //                       ]
    //                     : '-',
    //                 type: payload.type || payload.bene_type || '',
    //                 id:
    //                   payload.id ||
    //                   payload.bank_account_id ||
    //                   payload.contactId ||
    //                   '',
    //                 bank_account_name:
    //                   payload.name || payload.bank_account_name || '',
    //                 currencyid: payload.currencyid,
    //                 currency: payload.currency,
    //                 amount: payload.amount || payload.transferAmount || '',
    //                 bank_account_id: payload.bank_account_id || '',
    //                 country: payload.country || '',
    //                 bene_type: payload.bene_type || '',
    //                 bank_type: payload.bank_type || '',
    //                 contactId: payload.contactId,
    //                 transfer_fee:transfer_fee,
    //                 service_fee:service_fee
    //               },
    //             });
    //           });
    //       } else {
    //         const token = sessionStorage.getItem('login_token');
    //         const creditResponse = await axios.post(
    //           Constants.BASE_URL +
    //             'API-FX-250-CREATE-CREDIT-LINE-TRANSACTION-AGENT',
    //           {
    //             user_registered_domains_id: 1,
    //             amount:total_amount?Number(total_amount): Number(from_amount),
    //             type: 'debit',
    //           },
    //           {
    //             headers: {
    //               Authorization: 'Bearer ' + JSON.parse(token),
    //               fx_key: Constants.SUBSCRIPTION_KEY,
    //             },
    //           },
    //         );

    //         console.log('Credit Transaction:', creditResponse.data);
    // setLoading(false);
    //         navigate('/PaymentSuccess', {
    //           state: {
    //             name: payload.name || payload.bank_account_name || '',
    //             ifsc: payload.code || '',
    //             account: payload.account || '',
    //             type: payload.type || payload.bene_type || '',
    //             id:
    //               payload.id || payload.bank_account_id || payload.contactId || '',
    //             bank_account_name: payload.name || payload.bank_account_name || '',
    //             currencyid: payload.currencyid,
    //             currency: payload.currency,
    //             amount: payload.amount,
    //             bank_account_id: payload.bank_account_id,
    //             country: payload.country,
    //             bene_type: payload.bene_type,
    //             bank_type: payload.bank_type,
    //             contactId: payload.contactId,
    //             transactionId: payload.transactionId,
    //             paymentMethod: 'credit',
    //             availableCredit,
    //           },
    //         });
    //       }
    //     } catch (err) {
    // const errorMessage =
    //         err.response?.data?.message ||
    //         err.message ||
    //         'Something went wrong. Please try again.';
    //       toast.error(errorMessage);
    //       setLoading(false);
    //     }
  };

  // ── bank payment (Volt/Yapily) ──
  const gettransactionid = async (payload) => {
    const login_token = sessionStorage.getItem('login_token');
    const login_workspaces_id = sessionStorage.getItem('login_workspaces_id');

    try {
      const clearBankCustomerWalletNumber = sessionStorage.getItem('clearBankCustomerWalletNumber');
      const clearBankCustomerSortCode = sessionStorage.getItem('clearBankCustomerSortCode');

      const body = {
        workspace_id: login_workspaces_id,
        currency: payload.from_currency || 'GBP',
        amount: total_amount,
        type: getUserTypeFromSession(),
        second_beneficiary_bank_account_number: String(
          clearBankCustomerWalletNumber || Constants.ACCOUNT_DETAILS?.[0]?.GBP?.[0]?.['Account Number'],
        ),
        second_beneficiary_bank_code: String(
          clearBankCustomerSortCode || Constants.ACCOUNT_DETAILS?.[0]?.GBP?.[0]?.['Sort Code'],
        ),
        createTransaction: '0',
        transactionId: payload.transactionId,
        redirect_url: window.location.origin + '/YapilyStatus',
      };

      const resp = await fetch(
        Constants.BASE_URL + 'API-FX-239-GENRATE-VOLT-LINK-WITH-TRANSACTION',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            fx_key: Constants.SUBSCRIPTION_KEY,
            Authorization: 'Bearer ' + JSON.parse(login_token),
          },
          body: JSON.stringify(body),
        },
      );

      const data = await resp.json();
      const transactionid = data.credit_transaction_id
      console.log("credit transaction id", transactionid)
      return transactionid
      // setCredittransactionid(transactionid)
    } catch (err) {
      console.error('startBankPayment error:', err);
      setAvailableCredit(0);
      toast.error('Unable to start bank payment: ' + (err.message || err));
      setLoading(false);
    }
  };

  const startBankPayment = async (payload, credittransactionid) => {
    const login_workspace_id = sessionStorage.getItem('login_workspaces_id');
    
      try {
        const response = await axios.get(
          "https://banking.fxmaster.co.uk/api/yapily/institutions/",

          {
            headers: {
              accept: "*/*",
              "X-Correlation-Id": credittransactionid,
              "X-External-User-Id": login_workspace_id,
              "X-Idempotency-Key": credittransactionid,
            },
          }
        )
        console.log(response.data)
        navigate('/Banks', {
          state: {
            data: response.data,
            banks: response.data.banks,
            external_user_id: response.data.external_user_id,
            payload: payload,
            transactionid: credittransactionid,
            amount: payload?.from_amount,
            currency: payload?.from_currency,
            service_fee: payload?.service_fee,
            transfer_fee: payload?.transfer_fee,
          }
        })
      }
      catch (error) {
        console.error(error.response?.data || error.message);
        toast.error("Failed to fetch banks")
      }
  }

  const canContinue = !!s.name && !loading && !creditLoading;

  // ── render ──
  return (
    <div className="min-h-screen bg-gradient-to-b from-[#f8fafc] via-[#eff6ff4d] to-[#eef2ff66] py-8 px-4">
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} />

      <div className="max-w-[680px] mx-auto">
        {/* Header */}
        <div className="text-center">
          <h1 className="text-[28px] font-[700] text-[#1f2a44] leading-[34px]">Account Review</h1>
          <p className="text-[14px] text-[#7b8190] mt-1 font-[400]">Review of sending details</p>
        </div>

        {/* Back */}
        <button onClick={() => navigate(-1)} className="flex items-center mt-5 mb-5 text-[15px] font-[600] text-[#1f2a44]">
          <ArrowLeft className="w-4 mr-1 font-extrabold" />
          Back
        </button>

        {/* MAIN CARD */}
        <div className="bg-white border-[1.5px] border-[#e2e8f0] rounded-[12px] p-4 shadow-[0_1px_2px_rgba(0,0,0,0.04)]">

          {/* Payment Type */}
          <div>
            <p className="text-[13px] font-[600] text-[#1f2a44] mb-3">
              Select Payment Type
            </p>
            <div
              className={`grid ${isStaffAllowed ? 'grid-cols-2' : 'grid-cols-2'} gap-3`}
            >
              {/* BANK */}
              <div
                onClick={() => setMethod('bank')}
                className={`cursor-pointer rounded-[10px]  border-[1.5px] border-[#e2e8f0] px-3 py-3 transition-all ${method === 'bank' ? 'border-[#243b6b] bg-white' : 'border-[#dcdfe5]'}`}
              >
                <div className="flex bg-[#FCFCFC] gap-2">
                  <input type="radio" checked={method === 'bank'} readOnly className="mt-[2px] accent-[#081735]" />
                  <div>
                    <p className="text-[14px] font-[700] text-[#1f2a44]">Bank Transfer</p>
                    <p className="text-[11px] text-[#6b7280] leading-[16px] mt-[2px]">Faster Payments From Bank</p>
                  </div>
                </div>
              </div>

              {/* MANUAL */}
              <div
                onClick={() => setMethod('manual')}
                className={`cursor-pointer rounded-[10px] border-[1.5px] border-[#e2e8f0] px-3 py-3 transition-all ${method === 'manual' ? 'border-[#243b6b] bg-white' : 'border-[#dcdfe5]'}`}
              >
                <div className="flex gap-2">
                  <input type="radio" checked={method === 'manual'} readOnly className="mt-[2px] accent-[#081735]" />
                  <div className="bg-[#FCFCFC]">
                    <p className="text-[14px] font-[700] text-[#1f2a44]">Manual Transfer</p>
                    <p className="text-[11px] text-[#6b7280] leading-[16px] mt-[2px]">Transfer Funds Directly From Your Bank</p>
                  </div>
                </div>
              </div>
              {/* {isStaffAllowed && (
                <div
                  onClick={() => setMethod('card')}
                  className={`cursor-pointer rounded-[10px] border-[1.5px] border-[#e2e8f0] px-3 py-3 transition-all ${method === 'card' ? 'border-[#243b6b] bg-white' : 'border-[#dcdfe5]'}`}
                >
                  <div className="flex gap-2">
                    <input
                      type="radio"
                      checked={method === 'card'}
                      readOnly
                      className="mt-[2px] accent-[#081735]"
                    />
                    <div className="bg-[#FCFCFC]">
                      <p className="text-[14px] font-[700] text-[#1f2a44]">
                        Card Machine
                      </p>
                      <p className="text-[11px] text-[#6b7280] leading-[16px] mt-[2px]">
                        Transfer Funds Directly From Your Card
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {isStaffAllowed && (
                <div
                  onClick={() => {
                    setMethod('credit')
                    handlecredit()
                  }}
                  className={`cursor-pointer rounded-[10px] border-[1.5px] border-[#e2e8f0] px-3 py-3 transition-all ${method === 'credit' ? 'border-[#243b6b] bg-white' : 'border-[#dcdfe5]'}`}
                >
                  <div className="flex gap-2">
                    <input
                      type="radio"
                      checked={method === 'credit'}
                      readOnly
                      className="mt-[2px] accent-[#081735]"
                    />
                    <div className="bg-[#FCFCFC]">
                      <p className="text-[14px] font-[700] text-[#1f2a44]">
                        Credit Line
                      </p>
                      <p className="text-[11px] text-[#6b7280] leading-[16px] mt-[2px]">
                        Transfer Funds Directly From Your Credits
                      </p>
                    </div>
                  </div>
                </div>
              )} */}
            </div>
            {
              method === 'bank' && (
            <div
              className={`grid ${isStaffAllowed ? 'grid-cols-2' : 'grid-cols-2'} gap-3 my-5`}
            >
              {/* BANK */}
              {/* <div
                onClick={() => setSubMethod('partner')}
                className={`cursor-pointer rounded-[10px]  border-[1.5px] border-[#e2e8f0] px-3 py-3 transition-all ${subMethod === 'partner' ? 'border-[#243b6b] bg-white' : 'border-[#dcdfe5]'}`}
              >
                <div className="flex bg-[#FCFCFC] gap-2">
                  <input type="radio" checked={subMethod === 'partner'} readOnly className="mt-[2px] accent-[#081735]" />
                  <div>
                    <p className="text-[14px] font-[700] text-[#1f2a44]">Navigate to Partner</p>
                  </div>
                </div>
              </div> */}

              {/* MANUAL */}
              {/* <div
                onClick={() => setSubMethod('qr')}
                className={`cursor-pointer rounded-[10px] border-[1.5px] border-[#e2e8f0] px-3 py-3 transition-all ${subMethod === 'qr' ? 'border-[#243b6b] bg-white' : 'border-[#dcdfe5]'}`}
              >
                <div className="flex gap-2">
                  <input type="radio" checked={subMethod === 'qr'} readOnly className="mt-[2px] accent-[#081735]" />
                  <div className="bg-[#FCFCFC]">
                    <p className="text-[14px] font-[700] text-[#1f2a44]">Generate QR</p>
                  </div>
                </div>
              </div> */}
            </div>
            )}
          </div>

          {method === 'credit' && (
            <div className="mt-4 rounded-lg border bg-[#F8FAFC] p-4">
              <p className="text-sm text-gray-500">Available Balance</p>

              <h2 className="text-2xl font-bold text-green-600 mt-1">
                {getCurrencySymbol(from_currency)}
                {availableCredit}
              </h2>
            </div>
          )}

          {/* ACCOUNT DETAILS */}
          <div className="mt-4 border-[1.5px] border-[#e2e8f0] bg-[#FCFCFC] rounded-[12px] p-4">
            <h3 className="text-[14px] font-[700] text-[#1f2a44] mb-4">Account Details</h3>
            <div className="space-y-3">
              <div className="flex justify-between text-[13px]">
                <span className="text-[#1f2a44] font-[500]">You Send Amount</span>
                <span className="text-[#4b5563]">{getCurrencySymbol(from_currency)} {formattedAmount}</span>
              </div>
              {
                Number(transfer_fee) > 0 && (
                  <div className="flex justify-between text-[13px]">
                    <span className="text-[#1f2a44] font-[500]">Transfer Fee</span>
                    <span className="text-[#1f2a44] font-[500]">
                      {getCurrencySymbol(from_currency)} {parseFloat(transfer_fee).toFixed(2)}
                    </span>
                  </div>
                )
              }
              {isStaffAllowed && Number(service_fee) > 0 && (
                <div className="flex justify-between text-[13px]">
                  <span className="text-[#1f2a44] font-[500]">Service Fee</span>
                  <span className="text-[#1f2a44] font-[500]">
                    {getCurrencySymbol(from_currency)} {parseFloat(service_fee).toFixed(2)}
                  </span>
                </div>
              )}
              <div className="flex justify-between text-[13px]">
                <span className="text-[#1f2a44] font-[500]">Total Amount</span>
                <span className="text-[#1f2a44] font-[500]">
                  {getCurrencySymbol(from_currency)} {formattedTotalAmount}
                </span>
              </div>
              <div className="flex justify-between text-[13px]">
                <span className="text-[#1f2a44] font-[500]">Exchange rate</span>
                <span className="text-[#1f2a44] font-[500]">
                  1.00 {s.from_currency ?? ''} ={' '}
                  {parseFloat(exchangeRate).toFixed(4)} {s.currency ?? ''}
                </span>
              </div>

              <div className="flex justify-between text-[13px]">
                <span className="text-[#1f2a44] font-[500]">
                  Beneficiary Gets
                </span>
                <span className="text-[#1f2a44] font-[500]">
                  {recipientDisplay}
                </span>
              </div>
            </div>

            {/* BENEFICIARY */}
            <div className="mt-6">
              <h3 className="text-[14px] font-[700] text-[#1f2a44] mb-4">Beneficiary Details</h3>
              <div className="space-y-3">
                <div className="flex justify-between text-[13px]">
                  <span className="text-[#1f2a44] font-[500]">Beneficiary Name</span>
                  <span className="text-[#1f2a44] font-[500]">{s.name || '—'}</span>
                </div>

                <div className="flex justify-between text-[13px]">
                  <span className="text-[#1f2a44] font-[500]">
                    {currency === 'INR' && code === '-' ? 'UPI ID' : 'Account Number'}
                  </span>
                  <span className="text-[#1f2a44] font-[500]">
                    {s.account || '—'}
                  </span>
                </div>
                {code !== '-' && (
                  <div className="flex justify-between text-[13px]">
                    <span className="text-[#1f2a44] font-[500]">Code</span>
                    <span className="text-[#1f2a44] font-[500]">{s.code}</span>
                  </div>
                )}

                <div className="flex justify-between text-[13px]">
                  <span className="text-[#1f2a44] font-[500]">Country</span>
                  <span className="text-[#1f2a44] font-[500]">{s.country || 'India'}</span>
                </div>
                <div className="flex justify-between text-[13px]">
                  <span className="text-[#1f2a44] font-[500]">Account Type</span>
                  <span className="text-[#1f2a44] font-[500]">{s.account_type || 'Individual'}</span>
                </div>
              </div>
            </div>
          </div>

          {/* TRANSFER REASON */}
          <div className="mt-4">
            {currency !== 'INR' ? (
              <select
                onChange={(e) => setReasonForTransfer(e.target.value)}
                value={transferReason}
                className="w-full h-[44px] border-[1.5px] border-[#e2e8f0] bg-[#FCFCFC] rounded-[10px] px-3 text-[13px] text-[#374151] outline-none focus:border-[#243b6b] focus:ring-0 cursor-pointer"
              >
                <option value="">Select Transfer Reason</option>
                {transferReasons.map((option) => (
                  <option key={option.id} value={option.reason}>{option.reason}</option>
                ))}
              </select>
            ) : (
              <select
                onChange={(e) => setReasonForTransfer(e.target.value)}
                value={transferReason}
                className="w-full h-[44px] border border-[#e5e7eb] bg-[#FCFCFC] rounded-[10px] px-3 text-[13px] text-[#374151] outline-none focus:border-[#243b6b] focus:ring-0 cursor-pointer"
              >
                <option value="">Select Transfer Reason</option>
                {userType === 'individual'
                  ? Constants.INR_INDIVIDUAL_REASONS.map((option) => (
                    <option key={option.id} value={option.code + '_' + option.display_name}>{option.display_name}</option>
                  ))
                  : Constants.INR_BUSINESS_REASONS.map((option) => (
                    <option key={option.id} value={option.code + '_' + option.display_name}>{option.display_name}</option>
                  ))}
              </select>
            )}
          </div>

          {/* REFERENCE */}
          <input
            type="text"
            value={ref}
            onChange={(e) => {
              const sanitized = e.target.value.replace(/[^A-Za-z0-9 -]/g, '');
              setRef(sanitized);
            }}
            placeholder="Enter Payment Reference (Optional)"
            maxLength={35}
            className="w-full h-[44px] bg-[#FCFCFC] border-[1.5px] border-[#e2e8f0] rounded-[10px] px-3 mt-3 text-[13px] outline-none"
          />

          {/* DOCUMENT UPLOAD */}
          <div className="mt-5">
            <div className="flex items-center justify-between">
              <label className="text-[13px] font-[600] text-[#2563eb] cursor-pointer flex items-center gap-2">
                ⬆ Upload Documents
                <input
                  type="file"
                  multiple
                  hidden
                  ref={fileInputRef}
                  onChange={handleMultiFileUpload}
                />
              </label>
              {uploadedDocs.length > 0 && (
                <button onClick={() => setShowDocs(!showDocs)} className="text-[12px] text-[#6b7280] font-[500]">
                  {showDocs ? 'Hide ▲' : 'View ▼'}
                </button>
              )}
            </div>
            <p className="text-[11px] text-[#9CA3AF] mt-1">Supported Format: JPG, PNG or PDF — (Max 50MB)</p>

            {showDocs && uploadedDocs.length > 0 && (
              <div className="mt-4 overflow-hidden rounded-[10px] border border-[#ececec]">
                <table className="w-full text-[12px]">
                  <thead className="bg-[#fafafa]">
                    <tr className="text-left text-[#6b7280] border-b">
                      <th className="py-3 px-3">Sno</th>
                      <th className="py-3 px-3">Document</th>
                    </tr>
                  </thead>
                  <tbody>
                    {uploadedDocs.map((doc, index) => (
                      <tr key={doc.id} className="border-b last:border-b-0">
                        <td className="py-3 px-3">{index + 1}</td>
                        <td className="py-3 px-3">
                          <a href={doc.url} target="_blank" rel="noreferrer" className="text-[#2563eb] hover:underline">
                            {doc.name}
                          </a>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* CONTINUE BUTTON — now calls initiateWalletTransaction */}
        <div className="mt-4">
          <button
            className={`w-full h-[54px] rounded-[12px] text-white text-[16px] font-[700] transition-all flex items-center justify-center gap-2 ${!canContinue ? 'bg-gray-400 cursor-not-allowed' : 'bg-[#d86b1f]'
              }`}
            onClick={canContinue ? initiateWalletTransaction : undefined}
            disabled={!canContinue}
          >
            {loading || creditLoading ? (
              'Processing...'
            ) : (
              <>
                Continue
                <ArrowRight className="w-5 h-5 -ml-1" />
              </>
            )}
          </button>
        </div>

        {/* SECURITY BADGE */}
        <div className="mt-3 bg-[#f7efe9] border border-[#eadfd7] rounded-[14px] p-4 flex gap-3">
          <div className="text-[#d86b1f] text-[16px] mt-[2px]">🔒</div>
          <div>
            <p className="text-[13px] font-[700] text-[#1f2a44]">100% Secure &amp; Safe</p>
            <p className="text-[12px] text-[#7b8190] mt-1 leading-[18px]">
              Your money is protected with bank-level encryption. We never store your banking credentials.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}